import DialogComp from 'components/Dialog';
import UserForm from 'components/Forms/User';
import ToolTipComponent from 'components/ToolTip';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { EditIcon, PlusIcon, TrashIcon } from 'lucide-react';
import React from 'react';
import { v4 as uuidv4 } from 'uuid';

type TUserRequiredFields = 'firstNames' | 'lastNames' | 'uILanguage' | 'organization';

const hasRequiredFields = (data: Partial<TVaultUser>): data is Record<TUserRequiredFields, string> => {
  if ('firstNames' in data && data.firstNames && 'lastNames' in data && data.lastNames) return true;

  return false;
};

const UserList = ({ orgData, newUsers = [], onChangeUsers }: IUserList) => {
  const [filterString, setFilterString] = React.useState<string>('');

  const users = (orgData['users'] || []) as Optional<TVaultUser, 'capabilities' | 'accessGroups'>[];
  const accessGroups = orgData['accessGroups'] || [];

  const filteredUsers = (newUsers.length > 0 ? newUsers : users || []).filter(u => {
    if (!filterString) return u;
    return u.lastNames.toLowerCase().includes(filterString) || u.firstNames.toLowerCase().includes(filterString);
  });

  const [userEditData, setUserEditData] = React.useState<Partial<TVaultUser> | null>(null);

  const onAddNewUser = () => {
    if (!userEditData) return;
    const newUserEditData: TVaultUser & { action?: 'new' | 'remove' | 'edit' } = {
      ...userEditData,
      userId: userEditData.userId || uuidv4(),
      uILanguage: userEditData.uILanguage || 'fi',
      action: 'new',
      accessGroups: Array.isArray(userEditData.accessGroups) ? userEditData.accessGroups : [],
      capabilities:
        typeof userEditData.capabilities === 'string' && userEditData.capabilities
          ? (userEditData.capabilities as string).split(/,\s*/)
          : userEditData.capabilities || [],
      active: !!userEditData.active,
    } as TVaultUser & { action?: 'new' | 'remove' | 'edit' };
    onChangeUsers([...(newUsers.length > 0 ? newUsers : (users as Required<IOrg>['users'])), newUserEditData]);
    setUserEditData(null);
  };

  const onEditExistingUser = () => {
    if (!userEditData) return;

    const oldData = users.find(usr => usr.userId === userEditData.userId);
    const formattedNewData = {
      ...(userEditData.accessGroups
        ? {
            accessGroups: Array.isArray(userEditData.accessGroups) ? userEditData.accessGroups : [],
          }
        : {}),
      ...(userEditData.capabilities
        ? {
            capabilities:
              typeof userEditData.capabilities === 'string'
                ? (userEditData.capabilities as string).split(/,\s*/)
                : userEditData.capabilities,
          }
        : {}),
      ...(userEditData.active || userEditData.active === false
        ? {
            active: userEditData.active,
          }
        : {}),
    };

    const user = { ...oldData, ...userEditData, action: 'edit' as const, ...formattedNewData };
    const newUsersList = (newUsers.length > 0 ? newUsers : users).map(u => (u.userId === user.userId ? user : u));
    onChangeUsers(newUsersList as IOrg['users']);
    setUserEditData(null);
  };

  const deletedUsers = newUsers.filter(nu => nu.action === 'remove');

  return (
    <div>
      <div className="mb-2 flex items-end">
        <div>
          <Input
            type="text"
            id="userFilter"
            className="w-100"
            value={filterString}
            onChange={e => setFilterString(e.target.value)}
            placeholder="Search"
          />
        </div>
        <div className="flex w-full justify-end">
          <DialogComp
            openButton={
              <Button variant="outline" className="h-9">
                <PlusIcon strokeWidth={2} />
                <div>New user</div>
              </Button>
            }
            title="New user"
            content={
              <UserForm
                data={{}}
                newData={userEditData || {}}
                onChangeData={(field, data) => setUserEditData({ ...userEditData, [field]: data })}
                accessGroups={accessGroups}
              />
            }
            cancel={{ callback: () => setUserEditData({}) }}
            accept={{
              callback: () => onAddNewUser(),
              disabled: !userEditData || !hasRequiredFields(userEditData),
            }}
          />
        </div>
      </div>
      <div className="tableArea rounded-md">
        <table>
          <thead>
            <tr>
              <th>Last names</th>
              <th>First names</th>
              <th>Access groups</th>
              {/* <th>Capabilities</th> */}
              <th>Active</th>
              <th style={{ width: 'auto' }}></th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(usr => {
              const isDeleted = deletedUsers.find(du => du.userId === usr.userId);
              const isNew = newUsers.length > 0 && !users.find(nu => nu.userId === usr.userId);
              return (
                <tr key={usr.userId} className={isDeleted ? 'line-through' : isNew ? 'font-bold italic' : ''}>
                  <td>{usr.lastNames}</td>
                  <td>{usr.firstNames}</td>
                  <td>
                    {usr.accessGroups?.map(ac => (
                      <ToolTipComponent
                        key={ac}
                        trigger={(orgData.accessGroups || []).find(acgrp => acgrp.groupId === ac)?.groupName}
                        content={ac}
                      />
                    ))}
                  </td>
                  {/* <td>
                    {(usr.capabilities || [])?.length > 0 ? (
                      <ToolTipComponent
                        trigger={usr.capabilities?.length + ' capabilities'}
                        content={<div className="max-w-240">{usr.capabilities?.join(', ')}</div>}
                      />
                    ) : (
                      ''
                    )}
                  </td> */}
                  <td>{usr.active ? 'Yes' : 'No'}</td>
                  <td>
                    <div className="flex w-full justify-end">
                      <DialogComp
                        openButton={
                          <Button size={'icon'}>
                            <TrashIcon />
                          </Button>
                        }
                        title="Do you really want to delete this user?"
                        cancel={{ callback: () => '' }}
                        accept={{
                          callback: () => {
                            const newUsersList = (newUsers.length > 0 ? newUsers : users).map(u =>
                              u.userId === usr.userId ? { ...u, action: 'remove' as const } : u,
                            );
                            onChangeUsers(newUsersList as Required<IOrg>['users']);
                          },
                        }}
                      />

                      <DialogComp
                        openButton={
                          <Button size={'icon'} className="ml-2">
                            <EditIcon />
                          </Button>
                        }
                        title="Edit user"
                        content={
                          <UserForm
                            data={usr}
                            newData={userEditData || {}}
                            onChangeData={(field, data) =>
                              setUserEditData({ ...userEditData, userId: usr.userId, [field]: data })
                            }
                            accessGroups={accessGroups}
                          />
                        }
                        cancel={{ callback: () => setUserEditData(null) }}
                        accept={{
                          callback: () => onEditExistingUser(),
                          disabled: !userEditData || !hasRequiredFields({ ...usr, ...userEditData }),
                        }}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

interface IUserList {
  orgData: IOrg;
  newUsers?: IOrg['users'];
  onChangeUsers: (users: IOrg['users']) => void;
}

export default UserList;
